import find from "lodash/find";
import filter from "lodash/filter";
import compact from "lodash/compact";
import isEmpty from "lodash/isEmpty";
import isNull from "lodash/isNull";
import _orderBy from "lodash/orderBy";
import sortBy from "lodash/sortBy";

import { db } from "../firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "../../Common/utils/firebaseFunctions";
import {
  PriceDiscoveryVersion,
  ViewWLSubscriptionsTiers,
  WLSubscriptionsTiers,
} from "../../Common/models/subscriptions";
import { applyStripePriceVersionEntitlements } from "../../Common/utils/common";
import { SUBSCRIPTIONS } from "../constants/dbCollections";
import { getStripeSubscriptions } from "../api/stripe";
import { WLStripeSubscription } from "../../Common/models/stripe";
import { User } from "../../Common/models/user";
import { LEGEND_IDS, VIP_IDS } from "../../Common/config";
import {
  filterPlansForMembershipTrack,
  isClubPlan,
} from "../../Common/constants/clubMembership";
import { isPlatform } from "@ionic/react";

type SubscriptionListTiming = {
  onStep?: (label: string, durationMs: number) => void;
};

export const getSubscriptionListStripe = async (
  alternativePriceVersion = "version2" as PriceDiscoveryVersion, //PriceDiscoveryVersion,
  oneOffSignUp?: boolean,
  timing?: SubscriptionListTiming,
  options?: { includeClub?: boolean }
) => {
  let firestoreStart = Date.now();
  try {
    firestoreStart =
      typeof performance !== "undefined" ? performance.now() : Date.now();
  } catch {
    firestoreStart = Date.now();
  }

  const subscriptionQuery = await getDocs(
    query(collection(db, SUBSCRIPTIONS), where("platform", "==", "stripe"))
  );

  try {
    const durationMs = Math.round(
      (typeof performance !== "undefined" ? performance.now() : Date.now()) -
        firestoreStart
    );
    timing?.onStep?.("prerequisite: Firestore subscriptions", durationMs);
  } catch {
    // Timing callbacks must never break subscription loading.
  }

  const subscriptionConfigData = subscriptionQuery.docs.map(
    (doc) =>
      ({
        docId: doc.id,
        ...doc.data(),
      }) as WLSubscriptionsTiers
  );

  // const productQueryIdentifiers = subscriptionConfigData.map(
  //   (data) => data.productQueryIdentifier
  // );

  let stripeApiStart = Date.now();
  try {
    stripeApiStart =
      typeof performance !== "undefined" ? performance.now() : Date.now();
  } catch {
    stripeApiStart = Date.now();
  }

  const stripeSubscriptions = await getStripeSubscriptions(
    alternativePriceVersion
  );

  try {
    const durationMs = Math.round(
      (typeof performance !== "undefined" ? performance.now() : Date.now()) -
        stripeApiStart
    );
    timing?.onStep?.("prerequisite: /stripe/getSubscriptions", durationMs);
  } catch {
    // Timing callbacks must never break subscription loading.
  }
  const subscriptionObjects = stripeSubscriptions.data
    .result as WLStripeSubscription[];

  const result = filter(
    subscriptionObjects.map((product) => {
      const existingSubscriptionConfig = find(
        subscriptionConfigData,
        (sub) => sub.docId === product.id
      );

      if (existingSubscriptionConfig === undefined) {
        return null;
      }

      // Apply entitlements if alternativePriceVersion is used
      const subscriptionWithEntitlements = applyStripePriceVersionEntitlements(
        existingSubscriptionConfig,
        alternativePriceVersion
      );

      return {
        ...subscriptionWithEntitlements,
        isOneOff: !!existingSubscriptionConfig.isOneOff,
        stripePriceId: product.priceId,
        price: parseInt(product.price),
        priceString: `$${product.price}`,
        ...(!isNull(product.alternativePriceVersion) &&
        product.alternativePriceVersion !== undefined
          ? { alternativePriceVersion: product.alternativePriceVersion }
          : {}),
      } as ViewWLSubscriptionsTiers;
    }),
    (sub) =>
      !isNull(sub) &&
      (!!oneOffSignUp ? !!sub.isOneOff : !sub.isOneOff) &&
      sub.name.toLowerCase().trim() !== "vip" &&
      sub.name.toLowerCase().trim() !== "legend" &&
      (options?.includeClub === true || !isClubPlan(sub))
  );

  return compact(sortBy(result, "price"));
};

export const getSubscription = async (id: string) => {
  if (isEmpty(id)) {
    return null;
  }
  const query = await getDoc(doc(db, SUBSCRIPTIONS, id));
  if (!query.exists()) {
    return null;
  }

  return {
    docId: query.id,
    ...query.data(),
  } as WLSubscriptionsTiers;
};

export const getUserSubscriptionListStripe = async (
  user: undefined | null | User
) => {
  const subscriptionQuery = await getDocs(
    query(collection(db, SUBSCRIPTIONS), where("platform", "==", "stripe"))
  );

  const subscriptionConfigData = subscriptionQuery.docs.map(
    (doc) =>
      ({
        docId: doc.id,
        ...doc.data(),
      }) as WLSubscriptionsTiers
  );

  // const productQueryIdentifiers = subscriptionConfigData.map(
  //   (data) => data.productQueryIdentifier
  // );

  const stripeSubscriptions = await getStripeSubscriptions(
    user?.alternativePriceVersion
  );
  const subscriptionObjects = stripeSubscriptions.data
    .result as WLStripeSubscription[];

  const result = subscriptionObjects.map((product) => {
    const existingSubscriptionConfig = find(
      subscriptionConfigData,
      (sub) => sub.docId === product.id
    );

    if (existingSubscriptionConfig === undefined) {
      return null;
    }

    if (
      VIP_IDS.indexOf(existingSubscriptionConfig.docId) !== -1 &&
      user !== undefined &&
      !isNull(user) &&
      !user.vipQualified &&
      (!user.subscription || // no subscription
        (!!user.subscription && // subscription is not vip
          VIP_IDS.indexOf(user.subscription.docId)) === -1)
    ) {
      return null;
    }

    if (
      LEGEND_IDS.indexOf(existingSubscriptionConfig.docId) !== -1 &&
      user !== undefined &&
      !isNull(user) &&
      !user.legendQualified &&
      (!user.subscription || // no subscription
        (!!user.subscription && // subscription is not legend
          LEGEND_IDS.indexOf(user.subscription.docId)) === -1)
    ) {
      return null;
    }

    // Apply entitlements if user has alternativePriceVersion
    const subscriptionWithEntitlements = applyStripePriceVersionEntitlements(
      existingSubscriptionConfig,
      user?.alternativePriceVersion
    );

    return {
      ...subscriptionWithEntitlements,
      isOneOff: !!existingSubscriptionConfig.isOneOff,
      stripePriceId: product.priceId,
      price: parseInt(product.price),
      priceString: `$${product.price}`,
    } as ViewWLSubscriptionsTiers;
  });

  return filterPlansForMembershipTrack(
    compact(sortBy(result, "price")),
    user?.membershipTrack
  );
};
